// Augment the MRT_Row type from material-react-table
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { Box, IconButton, Theme, Tooltip } from '@mui/material'
import {
	MaterialReactTableProps,
	MRT_Cell,
	MRT_ColumnDef,
	MRT_ColumnOrderState,
	MRT_GlobalFilterTextField,
	MRT_Row,
	MRT_ShowHideColumnsButton,
	MRT_TableInstance,
	MRT_ToggleFiltersButton,
	MRT_ToggleGlobalFilterButton,
	MRT_VisibilityState,
	useMaterialReactTable,
} from 'material-react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { RowActionsCell } from '@/components'
import { useTableV2Ready } from '@/context/TableV2Context'
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation'

const COLUMN_ORDER_STORAGE_PREFIX = 'mrt-column-order-'
const COLUMN_VISIBILITY_STORAGE_PREFIX = 'mrt-column-visibility-'

const isStyleObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value)

const mergeTableSlotProps = <T,>(base?: T, override?: T): T | undefined => {
	if (!base) return override
	if (!override || typeof override === 'function') return base
	if (typeof base === 'function' || typeof base !== 'object') return override

	const baseProps = base as { sx?: unknown }
	const overrideProps = override as { sx?: unknown }

	return {
		...baseProps,
		...overrideProps,
		sx: {
			...(isStyleObject(baseProps.sx) ? baseProps.sx : {}),
			...(isStyleObject(overrideProps.sx) ? overrideProps.sx : {}),
		},
	} as T
}

const getStoredColumnOrder = (tableId: string): MRT_ColumnOrderState | null => {
	try {
		const stored = localStorage.getItem(`${COLUMN_ORDER_STORAGE_PREFIX}${tableId}`)
		return stored ? JSON.parse(stored) : null
	} catch {
		return null
	}
}

const saveColumnOrder = (tableId: string, columnOrder: MRT_ColumnOrderState) => {
	try {
		localStorage.setItem(`${COLUMN_ORDER_STORAGE_PREFIX}${tableId}`, JSON.stringify(columnOrder))
	} catch {
		// Ignore storage errors
	}
}

const getStoredColumnVisibility = (tableId: string): MRT_VisibilityState | null => {
	try {
		const stored = localStorage.getItem(`${COLUMN_VISIBILITY_STORAGE_PREFIX}${tableId}`)
		return stored ? JSON.parse(stored) : null
	} catch {
		return null
	}
}

const saveColumnVisibility = (tableId: string, columnVisibility: MRT_VisibilityState) => {
	try {
		localStorage.setItem(`${COLUMN_VISIBILITY_STORAGE_PREFIX}${tableId}`, JSON.stringify(columnVisibility))
	} catch {
		// Ignore storage errors
	}
}

type UseMRTTableInstanceProps<TData extends object> = {
	columns: MRT_ColumnDef<TData>[]
	customRowActions?:
		| ((props: {
				cell: MRT_Cell<TData, unknown>
				row: MRT_Row<TData>
				staticRowIndex?: number
				table: MRT_TableInstance<TData>
		  }) => React.ReactNode)
		| undefined
	data: TData[]
	deleteRow?: (rowId: number) => void
	editMode?: boolean
	isPanelOpen?: boolean
	isTechColumns?: boolean
	maxHeight?: string
	maxWidth?: string
	onInsertRow?: (rowIndex: number) => void
	options?: Partial<MaterialReactTableProps<TData>>
	selectedRowId?: number
	setSelectedRow?: (row: TData) => void
	/** Unique identifier for persisting column order to localStorage */
	tableId?: string
	/** When true, enable V2 table features (column order/visibility, toolbar search/filter/export). Overrides context. */
	v2Ready?: boolean
	withDefaults?: boolean
}

/**
 * Flexible hook to create an MRT Table instance.
 * - `withDefaults`: When `true` (default), includes common default table settings for MRT.
 * - Developers can override or skip defaults for full control.
 */
export const useTable = <TData extends object>({
	columns,
	customRowActions,
	data,
	deleteRow,
	editMode = false,
	isPanelOpen,
	isTechColumns = false,
	maxHeight = 'calc(100vh - 240px)',
	maxWidth = '100%',
	onInsertRow,
	options,
	selectedRowId,
	setSelectedRow,
	tableId,
	v2Ready: v2ReadyProp,
	withDefaults = true,
}: UseMRTTableInstanceProps<TData>) => {
	const { handleCellKeyDown } = useKeyboardNavigation()
	const v2ReadyFromContext = useTableV2Ready()
	const v2Ready = v2ReadyProp ?? v2ReadyFromContext

	// Initialize column order from localStorage if tableId is provided
	const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>(() => {
		if (tableId) {
			const stored = getStoredColumnOrder(tableId)
			if (stored) return stored
		}
		// Default: use column order from column definitions
		return columns.map(col => col.accessorKey as string ?? col.id ?? '').filter(Boolean)
	})

	// Update stored column order when columns change (new columns added)
	useEffect(() => {
		const columnIds = columns.map(col => col.accessorKey as string ?? col.id ?? '').filter(Boolean)
		// Add any new columns that aren't in the current order
		const newColumns = columnIds.filter(id => !columnOrder.includes(id))
		if (newColumns.length > 0) {
			setColumnOrder(prev => [...prev, ...newColumns])
		}
	}, [columns, columnOrder])

	// Handle column order change - save to localStorage
	const handleColumnOrderChange = useCallback((updater: MRT_ColumnOrderState | ((old: MRT_ColumnOrderState) => MRT_ColumnOrderState)) => {
		setColumnOrder(prev => {
			const newOrder = typeof updater === 'function' ? updater(prev) : updater
			if (tableId) {
				saveColumnOrder(tableId, newOrder)
			}
			return newOrder
		})
	}, [tableId])

	// Initialize column visibility from localStorage if tableId is provided
	const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(() => {
		if (tableId) {
			const stored = getStoredColumnVisibility(tableId)
			if (stored) return stored
		}
		return {} // All columns visible by default
	})

	// Handle column visibility change - save to localStorage
	const handleColumnVisibilityChange = useCallback((updater: MRT_VisibilityState | ((old: MRT_VisibilityState) => MRT_VisibilityState)) => {
		setColumnVisibility(prev => {
			const newVisibility = typeof updater === 'function' ? updater(prev) : updater
			if (tableId) {
				saveColumnVisibility(tableId, newVisibility)
			}
			return newVisibility
		})
	}, [tableId])
	// Merge default options with any user-defined options
	const tableOptions = useMemo(() => {
		const muiTableBodyRowProps = ({
			row,
		}: {
			row: MRT_Row<TData & { id?: string }>
		}) => {
			const rowOriginal = row.original
			const isSelected =
				selectedRowId &&
				rowOriginal.id &&
				selectedRowId === Number(rowOriginal.id)
			const shouldHighlight = isSelected && (isPanelOpen ?? true)

			return {
				onClick: () => {
					if (!isTechColumns) {
						setSelectedRow?.(rowOriginal)
					}
				},
				sx: {
					position: 'relative',
					height: '40px',
					backgroundColor: shouldHighlight
						? (theme: Theme) => theme.palette.emerald.main
						: undefined,
					'&:hover': {
						backgroundColor: editMode
							? shouldHighlight
								? (theme: Theme) => theme.palette.emerald.dark
								: (theme: Theme) => theme.palette.emerald.main
							: 'transparent !important',
					},
				},
			}
		}
		// Define default options for MRT
		const defaultOptions: Partial<MaterialReactTableProps<TData>> = withDefaults
			? {
					// ---------- DATA CONFIGURATION ----------
					columns,
					data,
					defaultColumn: {
						minSize: 40,
						maxSize: 1500,
						size: 200,
					},
					/*
					 * IMPORTANT: Provides stable row identification for Material React Table.
					 * Required for correct row deletion, selection, and UI synchronization.
					 *
					 * If UI doesn't sync with data state (wrong rows deleted/updated), check this property.
					 * See: https://www.material-react-table.com/docs/guides/state-management
					 */
					// MRT getRowId: row is generic TData (table-specific)
					getRowId: (row: any) => row.id,

					// ---------- TABLE LAYOUT & APPEARANCE ----------
					initialState: {
						density: 'compact',
						showColumnFilters: false, // Hidden by default, toggle with filter button
						showGlobalFilter: false, // Search via icon, not always visible
					},
					// Controlled state (persisted to localStorage)
					state: {
						columnOrder,
						columnVisibility,
					},
					onColumnOrderChange: handleColumnOrderChange,
					onColumnVisibilityChange: handleColumnVisibilityChange,
					// Show filter row under headers when toggled (like DataGridPro)
					columnFilterDisplayMode: 'subheader',
					enableFacetedValues: true, // Better filter options
					// We handle global filter manually in toolbar
					enableGlobalFilterRankedResults: false,
					layoutMode: 'grid',
					enableStickyHeader: true,
					muiTableContainerProps: {
						sx: {
							flex: '1 1 auto',
							maxHeight: 'unset',
							maxWidth,
							minHeight: 0,
							overflow: 'auto',
						},
					},
					muiBottomToolbarProps: {
						sx: { flex: '0 0 auto' },
					},

					// ---------- COLUMN FEATURE CONTROLS ----------
					enableColumnResizing: true,
					enableColumnFilters: true, // Filter panel like DataGridPro
					enableColumnPinning: false,
					enableSorting: true,
					enableRowActions: true,
					enableGlobalFilter: true, // Search in toolbar
					enableColumnActions: false, // No column menu in header
					enableHiding: true, // Column visibility toggle in toolbar
					enableDensityToggle: false, // No density selector
					enableColumnOrdering: true, // Drag to reorder columns
					enableColumnDragging: true, // Enable column drag

					// ---------- ROW INTERACTION CONTROLS ----------
					enableRowOrdering: true,
					muiTableBodyRowProps,
					positionActionsColumn: 'last',
				renderRowActions: customRowActions
					? customRowActions
					: ({ table, row }) => (
							<Box sx={{ width: onInsertRow ? 80 : 50, display: 'flex', gap: '0.5rem' }}>
								<RowActionsCell
									editMode={editMode}
									deleteRow={deleteRow}
									table={table}
									row={row}
									onInsertRow={onInsertRow}
								/>
							</Box>
						),

					// ---------- PAGINATION & TOOLBAR CONTROLS ----------
					enableBottomToolbar: true,
					enableTopToolbar: true, // Toolbar like DataGridPro
					enablePagination: false,
					renderBottomToolbar: false,
					enableFullScreenToggle: false,
					// Toolbar icons - matches DataGridPro
					positionToolbarAlertBanner: 'bottom',
					// Hide MRT's default global filter - we render our own in toolbar actions
					// MRT expects PositionGlobalFilterType; 'none' is valid but not in union in some versions
					positionGlobalFilter: 'none' as any,
					// Toolbar styling
					muiTopToolbarProps: {
						sx: {
							backgroundColor: 'transparent',
							minHeight: '48px',
							padding: '4px 8px',
							justifyContent: 'flex-end',
							'& .MuiBox-root': {
								gap: '4px',
								alignItems: 'center',
							},
							// Hide any MRT-rendered global filter (we render our own)
							'& > .MuiCollapse-root': {
								display: 'none',
							},
						},
					},
					// Search text field (appears when clicking search icon)
					muiSearchTextFieldProps: {
						size: 'small',
						variant: 'outlined',
						placeholder: 'Search...',
						sx: {
							minWidth: '150px',
							'& .MuiOutlinedInput-root': {
								height: '32px',
								fontSize: '13px',
							},
						},
					},
					// Filter text field styling (filter row under headers)
					muiFilterTextFieldProps: {
						size: 'small',
						variant: 'outlined',
						InputProps: {
							sx: {
								// Clear/close icon inside input - make it dark
								'& .MuiIconButton-root': {
									color: 'rgba(0, 0, 0, 0.7) !important',
								},
								'& .MuiSvgIcon-root': {
									color: 'rgba(0, 0, 0, 0.7) !important',
								},
							},
						},
						sx: {
							'& .MuiOutlinedInput-root': {
								height: '32px',
								fontSize: '13px',
								backgroundColor: '#fff',
							},
							// Ensure icons are dark
							'& .MuiIconButton-root': {
								color: 'rgba(0, 0, 0, 0.7) !important',
							},
							'& .MuiInputAdornment-root .MuiSvgIcon-root': {
								color: 'rgba(0, 0, 0, 0.7) !important',
							},
						},
					},
					// Filter row container styling
					muiFilterRowProps: {
						sx: {
							backgroundColor: '#f5f5f5',
							'& .MuiIconButton-root': {
								color: 'rgba(0, 0, 0, 0.7) !important',
							},
							'& .MuiSvgIcon-root': {
								color: 'rgba(0, 0, 0, 0.7) !important',
							},
						},
					},
					// Toolbar icons styling
					muiToolbarAlertBannerProps: {
						sx: { backgroundColor: 'transparent' },
					},
					// Toolbar icons - matches DataGridPro (columns, filter, export, search)
					// Search field positioned right before search icon - expands left, pushes icons
					renderToolbarInternalActions: ({ table }) => (
						<Box sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
							{/* Column visibility toggle - grid icon */}
							<MRT_ShowHideColumnsButton table={table} />
							{/* Filter toggle - funnel icon */}
							<MRT_ToggleFiltersButton table={table} />
							{/* Export button - download icon */}
							<Tooltip title="Export">
								<IconButton
									onClick={() => {
										// Export to CSV functionality
										const rows = table.getFilteredRowModel().rows
										const headers = table.getAllColumns()
											.filter(col => col.getIsVisible() && !['mrt-row-actions', 'mrt-row-drag'].includes(col.id))
											.map(col => col.columnDef.header)
										const csvContent = [
											headers.join(','),
											...rows.map(row =>
												table.getAllColumns()
													.filter(col => col.getIsVisible() && !['mrt-row-actions', 'mrt-row-drag'].includes(col.id))
													.map(col => {
														const value = row.getValue(col.id)
														return typeof value === 'string' && value.includes(',')
															? `"${value}"`
															: value ?? ''
													})
													.join(',')
											)
										].join('\n')
										const blob = new Blob([csvContent], { type: 'text/csv' })
										const url = URL.createObjectURL(blob)
										const a = document.createElement('a')
										a.href = url
										a.download = 'export.csv'
										a.click()
									}}
									size="small"
								>
									<FileDownloadIcon />
								</IconButton>
							</Tooltip>
							{/* Search field - slides in from right to left */}
							<Box
								sx={{
									display: 'flex',
									overflow: 'hidden',
									maxWidth: table.getState().showGlobalFilter ? '200px' : '0px',
									opacity: table.getState().showGlobalFilter ? 1 : 0,
									transition: 'max-width 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-out',
									'& .MuiTextField-root': {
										minWidth: '200px',
									},
								}}
							>
								<MRT_GlobalFilterTextField table={table} />
							</Box>
							{/* Search toggle - magnifying glass icon (rightmost) */}
							<MRT_ToggleGlobalFilterButton table={table} />
						</Box>
					),

					// ---------- STYLING CUSTOMIZATIONS ----------

					// NonEditable MRT TextFields
					muiTableBodyCellProps: ({ column, row }) => {
						const rowOriginal = row.original as { id?: number }
						const isRowSelected =
							selectedRowId &&
							rowOriginal.id &&
							selectedRowId === Number(rowOriginal.id)
						const shouldShowRowHighlight =
							isRowSelected && (isPanelOpen ?? true)

						return {
							sx: {
								fontSize: 14,
								padding: '4px 8px',
								borderBottom: '1px solid rgba(224, 224, 224, 1)',
								color: 'rgba(0, 0, 0, 0.87)',								backgroundColor: shouldShowRowHighlight
									? 'transparent'
									: !editMode
										? '#F2F2F2 !important'
										: column.columnDef.meta?.isCodeGenerated
											? '#F2F2F2'
											: 'transparent',
							},
							onKeyDown: handleCellKeyDown,
						}
					},
					muiTablePaperProps: {
						sx: {
							display: 'flex',
							elevation: 0,
							flexDirection: 'column',
							maxHeight,
							overflow: 'hidden',
							padding: '1rem 0.5rem;',
						},
					},
					muiTableHeadProps: {
						sx: { zIndex: 20 },
					},
					// Object Settings style - teal header with white text
					muiTableHeadCellProps: {
						sx: {
							backgroundColor: '#265560',
							color: '#fff',
							fontSize: '15px',
							fontWeight: 600,
							padding: '4px 8px',
							borderBottom: '1px solid rgba(224, 224, 224, 1)',
							// Sticky/grid layout promotes a compositing layer; grayscale AA keeps light-on-dark text sharp on macOS
							WebkitFontSmoothing: 'antialiased',
							MozOsxFontSmoothing: 'grayscale',
							// Sort label styling
							'& .Mui-TableHeadCell-Content': {
								justifyContent: 'space-between',
							},
							'& .Mui-TableHeadCell-Content-Labels': {
								color: '#fff',
								WebkitFontSmoothing: 'antialiased',
								MozOsxFontSmoothing: 'grayscale',
							},
							'& .Mui-TableHeadCell-Content-Wrapper': {
								color: '#fff',
								WebkitFontSmoothing: 'antialiased',
								MozOsxFontSmoothing: 'grayscale',
							},
							'& .MuiTableSortLabel-root': {
								color: '#fff !important',
							},
							'& .MuiTableSortLabel-icon': {
								color: '#fff !important',
								opacity: 1,
							},
							// Icons in header (filter, sort) - white
							'& .MuiSvgIcon-root': {
								color: '#fff !important',
							},
							'& .MuiIconButton-root': {
								color: '#fff !important',
							},
							// But filter INPUT icons inside the filter textfield - dark
							'& .MuiTextField-root .MuiSvgIcon-root': {
								color: 'rgba(0, 0, 0, 0.7) !important',
							},
							'& .MuiTextField-root .MuiIconButton-root': {
								color: 'rgba(0, 0, 0, 0.7) !important',
							},
							'& .MuiInputAdornment-root .MuiSvgIcon-root': {
								color: 'rgba(0, 0, 0, 0.7) !important',
							},
						},
					},
					displayColumnDefOptions: {
						'mrt-row-actions': {
							size: 70, // Set fixed width for actions column
							maxSize: 150, // Limit maximum width
							minSize: 50, // Ensure minimum width
						},
						'mrt-row-drag': {
							header: '',
							maxSize: 20,
						},
					},
					muiSkeletonProps: {
						animation: 'pulse',
						height: 28,
					},
				}
			: {}

		// When v2Ready is false, use exact pre-V2 table behaviour (same as before branch)
		const legacyRowProps = ({
			row,
		}: {
			row: MRT_Row<TData & { id?: string }>
		}) => {
			const rowOriginal = row.original
			const isSelected =
				selectedRowId &&
				rowOriginal.id &&
				selectedRowId === Number(rowOriginal.id)
			const shouldHighlight = isSelected && (isPanelOpen ?? true)
			return {
				onClick: () => {
					if (!isTechColumns) {
						setSelectedRow?.(rowOriginal)
					}
				},
				sx: {
					height: '40px',
					backgroundColor: shouldHighlight
						? (theme: Theme) => theme.palette.emerald.main
						: undefined,
					'&:hover': {
						backgroundColor: editMode
							? shouldHighlight
								? (theme: Theme) => theme.palette.emerald.dark
								: (theme: Theme) => theme.palette.emerald.main
							: 'transparent !important',
					},
				},
			}
		}
		const legacyOverrides: Partial<MaterialReactTableProps<TData>> = !v2Ready
			? {
					state: undefined,
					onColumnOrderChange: undefined,
					onColumnVisibilityChange: undefined,
					enableColumnOrdering: false,
					enableColumnDragging: false,
					enableHiding: false,
					enableGlobalFilter: false,
					enableColumnFilters: true,
					enableColumnPinning: true,
					enableColumnActions: true, // Restore column header dots menu (filter/sort/visibility per column)
					columnFilterDisplayMode: undefined,
					enableFacetedValues: false,
					enableGlobalFilterRankedResults: undefined,
					renderToolbarInternalActions: undefined,
					positionGlobalFilter: undefined,
					muiSearchTextFieldProps: undefined,
					muiFilterTextFieldProps: undefined,
					muiFilterRowProps: undefined,
					initialState: { density: 'compact' as const },
					enableTopToolbar: false,
					enableFullScreenToggle: undefined,
					positionToolbarAlertBanner: undefined,
					muiTopToolbarProps: undefined,
					muiToolbarAlertBannerProps: undefined,
					muiTableBodyRowProps: legacyRowProps,
					muiTableBodyCellProps: ({ column, row }) => {
						const rowOriginal = row.original as { id?: number }
						const isRowSelected =
							selectedRowId &&
							rowOriginal.id &&
							selectedRowId === Number(rowOriginal.id)
						const shouldShowRowHighlight =
							isRowSelected && (isPanelOpen ?? true)
						return {
							sx: {
								fontSize: 14,
								padding: '0 6px',
								backgroundColor: shouldShowRowHighlight
									? 'transparent'
									: !editMode
										? '#F2F2F2 !important'
										: column.columnDef.meta?.isCodeGenerated
											? '#F2F2F2'
											: 'transparent',
							},
						}
					},
					muiTableHeadCellProps: {
						sx: { fontSize: '14px', fontWeight: 'normal' },
					},
					renderRowActions: customRowActions
						? customRowActions
						: ({ table, row }) => (
								<Box sx={{ width: 50, display: 'flex', gap: '1rem' }}>
									<RowActionsCell
										editMode={editMode}
										deleteRow={deleteRow}
										table={table}
										row={row}
									/>
								</Box>
							),
				}
			: {}

		const mergedOptions = {
			...defaultOptions,
			...legacyOverrides,
			...options, // Allow developers to overwrite defaults via options
			columns,
			data,
		}

		return {
			...mergedOptions,
			muiTablePaperProps: mergeTableSlotProps(
				defaultOptions.muiTablePaperProps,
				options?.muiTablePaperProps,
			),
			muiTableContainerProps: mergeTableSlotProps(
				defaultOptions.muiTableContainerProps,
				options?.muiTableContainerProps,
			),
			muiBottomToolbarProps: mergeTableSlotProps(
				defaultOptions.muiBottomToolbarProps,
				options?.muiBottomToolbarProps,
			),
		}
	}, [
		columnOrder,
		columnVisibility,
		columns,
		customRowActions,
		data,
		deleteRow,
		editMode,
		handleCellKeyDown,
		isPanelOpen,
		handleColumnOrderChange,
		handleColumnVisibilityChange,
		isTechColumns,
		maxHeight,
		maxWidth,
		onInsertRow,
		options,
		selectedRowId,
		setSelectedRow,
		tableId,
		v2Ready,
		withDefaults,
	])

	return useMaterialReactTable(tableOptions)
}
